// =====================================================
// 产品层级管理页面
// 支持面包屑导航（默认）和树形视图切换
// 参考 Apple 产品层级设计
// =====================================================

import React, { useEffect, useState } from 'react';
import { 
  Grid3X3, 
  Plus, 
  RefreshCw, 
  Download,
  Upload,
  Settings,
} from 'lucide-react';
import { useHierarchyStore } from '../stores/hierarchy-store';
import { 
  Breadcrumb, 
  BreadcrumbWithBack 
} from '../components/hierarchy/Breadcrumb';
import { TreeView, TreeActions } from '../components/hierarchy/TreeView';
import { ViewToggle } from '../components/hierarchy/ViewToggle';
import { HierarchyList } from '../components/hierarchy/HierarchyList';
import { CategoryLabels } from '../types/hierarchy';

// =====================================================
// 主组件
// =====================================================

export function ProductHierarchyPage() {
  const {
    viewMode,
    setViewMode,
    breadcrumbs,
    treeData,
    expandedNodes,
    selectedCategory,
    selectedSeries,
    selectedFamily,
    selectedModel,
    isLoading,
    isLoadingTree,
    error,
    navigateToLevel,
    navigateBack,
    toggleTreeNode,
    selectTreeNode,
    fetchTreeData,
    clearError,
  } = useHierarchyStore();

  // 当前层级深度
  const currentLevel = selectedModel ? 4 
    : selectedFamily ? 3 
    : selectedSeries ? 2 
    : selectedCategory ? 1 
    : 0;

  // 初始化树形数据
  useEffect(() => {
    if (viewMode === 'tree' && treeData.length === 0) {
      fetchTreeData();
    }
  }, [viewMode, treeData.length, fetchTreeData]);

  // 展开所有节点
  const handleExpandAll = () => {
    // 递归获取所有节点 ID
    const getAllNodeIds = (nodes: typeof treeData): string[] => {
      return nodes.flatMap(node => [
        node.id,
        ...(node.children ? getAllNodeIds(node.children) : []),
      ]);
    };
    const allIds = getAllNodeIds(treeData);
    allIds.forEach(id => {
      if (!expandedNodes.has(id)) {
        toggleTreeNode(id);
      }
    });
  };

  // 收起所有节点
  const handleCollapseAll = () => {
    expandedNodes.forEach(id => toggleTreeNode(id));
  };

  // 刷新树形数据
  const handleRefresh = () => {
    fetchTreeData();
  };

  // 获取当前层级标题
  const getPageTitle = () => {
    if (selectedModel) return `${selectedModel.name} - SKU 管理`;
    if (selectedFamily) return `${selectedFamily.name} - 型号管理`;
    if (selectedSeries) return `${selectedSeries.name} - 产品族管理`;
    if (selectedCategory) return `${CategoryLabels[selectedCategory]} - 系列管理`;
    return '产品层级管理';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Grid3X3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{getPageTitle()}</h1>
                <p className="text-sm text-gray-500">
                  5 级层级架构管理
                </p>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center gap-2">
              {/* 导入 CSV */}
              <button
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="导入 CSV"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">导入</span>
              </button>
              
              {/* 导出 */}
              <button
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="导出数据"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">导出</span>
              </button>

              {/* 视图切换 */}
              <ViewToggle mode={viewMode} onChange={setViewMode} />

              {/* 添加按钮 */}
              <button
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
              >
                <Plus className="w-4 h-4" />
                添加
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <BreadcrumbWithBack
            items={breadcrumbs}
            onNavigate={navigateToLevel}
            onBack={navigateBack}
            canGoBack={currentLevel > 0}
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-red-700 text-sm">{error}</span>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {viewMode === 'tree' ? (
          // 树形视图模式
          <div className="flex gap-6">
            {/* 左侧：树形导航 */}
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-24">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">产品层级</h3>
                  <button
                    onClick={handleRefresh}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    title="刷新"
                  >
                    <RefreshCw className={`w-4 h-4 text-gray-400 ${isLoadingTree ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                
                <TreeActions
                  onExpandAll={handleExpandAll}
                  onCollapseAll={handleCollapseAll}
                  onRefresh={handleRefresh}
                />
                
                <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                  <TreeView
                    data={treeData}
                    selectedId={
                      selectedModel?.id || 
                      selectedFamily?.id || 
                      selectedSeries?.id || 
                      selectedCategory || 
                      undefined
                    }
                    expandedNodes={expandedNodes}
                    onSelect={selectTreeNode}
                    onToggle={toggleTreeNode}
                    isLoading={isLoadingTree}
                  />
                </div>
              </div>
            </div>

            {/* 右侧：详情面板 */}
            <div className="flex-1">
              <DetailPanel />
            </div>
          </div>
        ) : (
          // 面包屑导航模式（默认）
          <HierarchyList />
        )}
      </div>

      {/* 层级统计 Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span>当前层级: {currentLevel + 1}/5</span>
            <span>|</span>
            <span>视图模式: {viewMode === 'breadcrumb' ? '面包屑导航' : '树形视图'}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Level 1: 分类</span>
            <span>→</span>
            <span>Level 2: 系列</span>
            <span>→</span>
            <span>Level 3: 产品族</span>
            <span>→</span>
            <span>Level 4: 型号</span>
            <span>→</span>
            <span>Level 5: SKU</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// =====================================================
// 详情面板（树形视图右侧）
// =====================================================

function DetailPanel() {
  const {
    selectedCategory,
    selectedSeries,
    selectedFamily,
    selectedModel,
    selectedSKU,
    skusList,
    modelsList,
    familiesList,
  } = useHierarchyStore();

  // 根据选中状态显示不同内容
  if (selectedModel) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {selectedModel.name} - SKU 列表
        </h2>
        {skusList.length === 0 ? (
          <p className="text-gray-500">暂无 SKU 数据</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {skusList.map((sku) => (
              <div
                key={sku.id}
                className={`p-3 rounded-lg border ${
                  sku.is_default ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{sku.model_name}</span>
                  {sku.is_default && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      默认
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 space-y-1">
                  {sku.brightness_typical && <p>亮度: {sku.brightness_typical}nit</p>}
                  {sku.panel_width && sku.panel_height && (
                    <p>箱体: {sku.panel_width}×{sku.panel_height}mm</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (selectedFamily) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-32 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
            {selectedFamily.image_url ? (
              <img
                src={selectedFamily.image_url}
                alt={selectedFamily.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600">
                无图片
              </div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{selectedFamily.name}</h2>
            {selectedFamily.slogan && (
              <p className="text-gray-500 italic">"{selectedFamily.slogan}"</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              {selectedFamily.process_type && <span>{selectedFamily.process_type}</span>}
              {selectedFamily.pitch_range && (
                <span className="text-blue-600 font-medium">{selectedFamily.pitch_range}</span>
              )}
            </div>
          </div>
        </div>
        
        <h3 className="font-medium text-gray-900 mb-3">产品型号 ({modelsList.length})</h3>
        {modelsList.length === 0 ? (
          <p className="text-gray-500">暂无型号数据</p>
        ) : (
          <div className="space-y-2">
            {modelsList.map((model) => (
              <div
                key={model.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <span className="font-medium">{model.name}</span>
                  {model.pitch && (
                    <span className="ml-2 text-sm text-blue-600">P{model.pitch}</span>
                  )}
                </div>
                <span className="text-sm text-gray-400">
                  {model.sku_count || 0} SKUs
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (selectedSeries) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{selectedSeries.name}</h2>
        {selectedSeries.description && (
          <p className="text-gray-500 mb-4">{selectedSeries.description}</p>
        )}
        
        <h3 className="font-medium text-gray-900 mb-3">产品族 ({familiesList.length})</h3>
        {familiesList.length === 0 ? (
          <p className="text-gray-500">暂无产品族数据</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {familiesList.map((family) => (
              <div
                key={family.id}
                className="p-3 bg-gray-50 rounded-lg"
              >
                <div className="font-medium">{family.name}</div>
                <div className="text-sm text-gray-500">
                  {family.model_count || 0} 型号 · {family.sku_count || 0} SKUs
                </div>
                {family.pitch_range && (
                  <div className="text-sm text-blue-600 mt-1">{family.pitch_range}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (selectedCategory) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {CategoryLabels[selectedCategory]} 分类
        </h2>
        <p className="text-gray-500">
          请从左侧树形视图中选择一个产品系列查看详情。
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
      <p className="text-gray-500">
        请从左侧树形视图中选择一个节点查看详情。
      </p>
    </div>
  );
}

export default ProductHierarchyPage;
